import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { deepGet, deepSet, isCurrencyField } from '../utils/dynamicFormUtils';
import { getRequiredFieldsGsn } from '../api/payoutMethodApi';

/** Chờ user ngừng gõ trước khi gọi getRequiredFieldsGsn (field refresh) — tránh gọi mỗi ký tự (IBAN, SWIFT…). */
const REFRESH_DEBOUNCE_MS = 1000;

/** AirWallex: path đổi từ transfer_methods (lần đầu) sang transfer_method (sau khi chọn) – giữ lại value khi refresh. */
const PATH_MIGRATION = { transfer_method: 'transfer_methods' };

function isWiseMultiSchema(s) {
  return Array.isArray(s?.wiseVariants) && s.wiseVariants.length > 0;
}

/**
 * Build initial formData from schema: set defaults for disabled fields, leave enabled fields empty.
 * @param {import('../utils/fieldSchemaTypes').FieldSchema[]} fields
 * @returns {Object}
 */
function buildInitialFormData(fields) {
  const formData = {};
  if (!Array.isArray(fields)) return formData;
  fields.forEach((item) => {
    if (!item.path) return;
    if (item.enabled === false && item.field?.default !== undefined) {
      deepSet(formData, item.path, item.field.default);
    }
  });
  return formData;
}

/**
 * @param {Object} next — mutated in place
 * @param {import('../utils/fieldSchemaTypes').FieldSchema[]} fields
 * @param {string} [currencyISO]
 */
function applySchemaDefaultsToForm(next, fields, currencyISO) {
  if (!next || !Array.isArray(fields)) return;
  Object.entries(PATH_MIGRATION).forEach(([newPath, oldPath]) => {
    const existing = deepGet(next, newPath);
    if (existing !== undefined && existing !== null && existing !== '') return;
    const valueFromOld = deepGet(next, oldPath);
    if (valueFromOld !== undefined && valueFromOld !== null) {
      const normalized = Array.isArray(valueFromOld) ? valueFromOld[0] : valueFromOld;
      deepSet(next, newPath, normalized);
    }
  });
  if (currencyISO) {
    fields.forEach((item) => {
      if (isCurrencyField(item)) deepSet(next, item.path, currencyISO);
    });
  }
  fields.forEach((item) => {
    if (item.enabled === false && item.field?.default !== undefined) {
      const existing = deepGet(next, item.path);
      if (existing === undefined) deepSet(next, item.path, item.field.default);
    }
  });
}

/**
 * @param {{ fleetId: string, type?: string, gateway?: string }} options
 */
export function useDynamicFormResolver(options = {}) {
  const { fleetId, type = 'fleet', gateway, notification, loadingActions, currencyISO } = options;
  const [schema, setSchema] = useState(null);
  const [formData, setFormDataState] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [refreshPending, setRefreshPending] = useState(false);
  const [wiseActiveVariantKey, setWiseActiveVariantKey] = useState(null);

  const formSnapshotsRef = useRef({});
  const wiseActiveVariantKeyRef = useRef(null);
  useEffect(() => {
    wiseActiveVariantKeyRef.current = wiseActiveVariantKey;
  }, [wiseActiveVariantKey]);

  const refreshTimeoutRef = useRef(null);
  const refreshInFlightRef = useRef(false);
  const lastRefreshPayloadRef = useRef(null);

  const activeSchema = useMemo(() => {
    if (!schema) return null;
    if (isWiseMultiSchema(schema)) {
      const list = schema.wiseVariants;
      const key = wiseActiveVariantKey || list[0]?.variantKey;
      return list.find((v) => v.variantKey === key) || list[0] || null;
    }
    return schema;
  }, [schema, wiseActiveVariantKey]);

  const mergeSnapshotsAfterMultiRefresh = useCallback(
    (response, mergedActiveForm) => {
      const variants = response?.wiseVariants ?? [];
      const activeKey = wiseActiveVariantKeyRef.current;
      variants.forEach((v) => {
        const key = v.variantKey;
        const fields = v.fields ?? [];
        if (key === activeKey) {
          formSnapshotsRef.current[key] = mergedActiveForm;
          return;
        }
        const prev = formSnapshotsRef.current[key] || {};
        const snap = JSON.parse(JSON.stringify(prev));
        applySchemaDefaultsToForm(snap, fields, currencyISO);
        formSnapshotsRef.current[key] = snap;
      });
    },
    [currencyISO]
  );

  const selectWiseVariant = useCallback(
    (newKey) => {
      if (!schema || !isWiseMultiSchema(schema)) return;
      const oldKey = wiseActiveVariantKeyRef.current;
      if (newKey === oldKey) return;
      if (oldKey) {
        formSnapshotsRef.current[oldKey] = JSON.parse(JSON.stringify(formData));
      }
      const variant = schema.wiseVariants.find((v) => v.variantKey === newKey);
      let nextForm = JSON.parse(
        JSON.stringify(formSnapshotsRef.current[newKey] || buildInitialFormData(variant?.fields || []))
      );
      const holderFromPrev = oldKey ? deepGet(formData, 'accountHolderName') : undefined;
      if (
        holderFromPrev &&
        (!deepGet(nextForm, 'accountHolderName') || deepGet(nextForm, 'accountHolderName') === '')
      ) {
        deepSet(nextForm, 'accountHolderName', holderFromPrev);
      }
      wiseActiveVariantKeyRef.current = newKey;
      setWiseActiveVariantKey(newKey);
      setFormDataState(nextForm);
      setErrors({});
    },
    [schema, formData]
  );

  const fetchSchema = useCallback(
    async (condition = null) => {
      if (!fleetId) return;
      const isInitial = schema === null;
      if (isInitial) setLoading(true);
      else setRefreshLoading(true);
      try {
        loadingActions?.showLoadingSpiner();
        const response = await getRequiredFieldsGsn({
          fleetId,
          type,
          gateway,
          ...(condition?.formData &&
            condition?.fields?.length && { formData: condition.formData, fields: condition.fields }),
        });
        setSchema(response);

        if (isWiseMultiSchema(response)) {
          formSnapshotsRef.current = {};
          response.wiseVariants.forEach((v) => {
            formSnapshotsRef.current[v.variantKey] = buildInitialFormData(v.fields ?? []);
          });
          const firstKey = response.wiseVariants[0]?.variantKey;
          wiseActiveVariantKeyRef.current = firstKey;
          setWiseActiveVariantKey(firstKey);
          setFormDataState(
            JSON.parse(JSON.stringify(formSnapshotsRef.current[firstKey] || {}))
          );
          return;
        }

        setWiseActiveVariantKey(null);
        wiseActiveVariantKeyRef.current = null;
        formSnapshotsRef.current = {};
        setFormDataState((prev) => {
          const next = JSON.parse(JSON.stringify(prev));
          const fields = response?.fields ?? [];
          fields.forEach((item) => {
            if (item.enabled === false && item.field?.default !== undefined) {
              const existing = deepGet(next, item.path);
              if (existing === undefined) deepSet(next, item.path, item.field.default);
            }
          });
          if (currencyISO) {
            fields.forEach((item) => {
              if (isCurrencyField(item)) deepSet(next, item.path, currencyISO);
            });
          }
          return next;
        });
      } catch (err) {
        console.error('getRequiredFieldsGsn failed', err);
        notification?.('error', err?.message || 'Failed to load form');
      } finally {
        setLoading(false);
        setRefreshLoading(false);
        loadingActions?.hideLoadingSpiner();
      }
    },
    [fleetId, type, gateway, schema === null, notification, currencyISO]
  );

  const setFieldValue = useCallback((path, value) => {
    setFormDataState((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      deepSet(next, path, value);
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
  }, []);

  const triggerRefresh = useCallback(
    (currentFormData, fields = [], currentSchema = null) => {
      const payload = JSON.stringify({ formData: currentFormData, fields: fields?.length });
      if (refreshInFlightRef.current) return;
      if (lastRefreshPayloadRef.current === payload) return;

      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      refreshTimeoutRef.current = setTimeout(async () => {
        refreshTimeoutRef.current = null;
        if (refreshInFlightRef.current) return;
        lastRefreshPayloadRef.current = payload;
        refreshInFlightRef.current = true;
        setRefreshLoading(true);
        try {
          loadingActions?.showLoadingSpiner();
          const response = await getRequiredFieldsGsn({
            fleetId,
            type,
            gateway,
            formData: currentFormData,
            fields,
            schema: currentSchema,
          });
          lastRefreshPayloadRef.current = null;
          setSchema(response);

          if (isWiseMultiSchema(response)) {
            const keys = new Set(response.wiseVariants.map((v) => v.variantKey));
            let activeKey = wiseActiveVariantKeyRef.current;
            if (!activeKey || !keys.has(activeKey)) {
              activeKey = response.wiseVariants[0]?.variantKey;
              wiseActiveVariantKeyRef.current = activeKey;
              setWiseActiveVariantKey(activeKey);
              const firstVar = response.wiseVariants[0];
              const next = JSON.parse(
                JSON.stringify(buildInitialFormData(firstVar?.fields ?? []))
              );
              applySchemaDefaultsToForm(next, firstVar?.fields ?? [], currencyISO);
              mergeSnapshotsAfterMultiRefresh(response, next);
              setFormDataState(JSON.parse(JSON.stringify(next)));
              return;
            }
            const next = JSON.parse(JSON.stringify(currentFormData));
            const activeVariant = response.wiseVariants.find((v) => v.variantKey === activeKey);
            const nextFields = activeVariant?.fields ?? [];
            applySchemaDefaultsToForm(next, nextFields, currencyISO);
            mergeSnapshotsAfterMultiRefresh(response, next);
            setFormDataState(JSON.parse(JSON.stringify(next)));
            return;
          }

          setFormDataState((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const nextFields = response?.fields ?? [];
            Object.entries(PATH_MIGRATION).forEach(([newPath, oldPath]) => {
              const existing = deepGet(next, newPath);
              if (existing !== undefined && existing !== null && existing !== '') return;
              const valueFromOld = deepGet(next, oldPath);
              if (valueFromOld !== undefined && valueFromOld !== null) {
                const normalized = Array.isArray(valueFromOld) ? valueFromOld[0] : valueFromOld;
                deepSet(next, newPath, normalized);
              }
            });
            if (currencyISO) {
              nextFields.forEach((item) => {
                if (isCurrencyField(item)) deepSet(next, item.path, currencyISO);
              });
            }
            nextFields.forEach((item) => {
              if (item.enabled === false && item.field?.default !== undefined) {
                const existing = deepGet(next, item.path);
                if (existing === undefined) deepSet(next, item.path, item.field.default);
              }
            });
            return next;
          });
        } catch (err) {
          console.error('getRequiredFieldsGsn refresh failed', err);
          lastRefreshPayloadRef.current = null;
          notification?.('error', err?.message || 'Failed to update form');
        } finally {
          refreshInFlightRef.current = false;
          setRefreshLoading(false);
          setRefreshPending(false);
          loadingActions?.hideLoadingSpiner();
        }
      }, REFRESH_DEBOUNCE_MS);
    },
    [fleetId, type, gateway, notification, currencyISO, mergeSnapshotsAfterMultiRefresh]
  );

  const validate = useCallback(() => {
    const fields = activeSchema?.fields ?? [];
    const newErrors = {};
    fields.forEach((item) => {
      if (!item.enabled) return;
      const value = deepGet(formData, item.path);
      const isEmpty = value === undefined || value === null || value === '';
      if (item.required && isEmpty) {
        newErrors[item.path] = 'Required';
        return;
      }
      if (!item.rule?.pattern) return;
      const pattern = item.rule.pattern;
      const re = new RegExp(pattern);
      const strVal = value != null ? String(value) : '';
      if (strVal && !re.test(strVal)) {
        newErrors[item.path] = 'Invalid format';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [activeSchema, formData]);

  const resetForm = useCallback(() => {
    setSchema(null);
    setFormDataState({});
    setErrors({});
    setWiseActiveVariantKey(null);
    wiseActiveVariantKeyRef.current = null;
    formSnapshotsRef.current = {};
    setRefreshLoading(false);
    setRefreshPending(false);
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    refreshInFlightRef.current = false;
    lastRefreshPayloadRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, []);

  return {
    schema,
    activeSchema,
    isWiseMultiVariant: Boolean(schema && isWiseMultiSchema(schema)),
    wiseActiveVariantKey,
    selectWiseVariant,
    formData,
    setFieldValue,
    errors,
    setErrors,
    loading,
    refreshLoading,
    refreshPending,
    fetchSchema,
    triggerRefresh,
    validate,
    resetForm,
  };
}
